// TUT-525 — a régua da ficha de solicitação do Portal do advogado (D25): o
// espelho de `recusaDoPedidoDoPortal` (`apps/api/src/domain/service-requests.ts`).
//
// A tela confere ANTES de subir anexo — senão o pedido recusado deixaria o
// arquivo no drive do advogado sem solicitação nenhuma —, e o modo
// demonstração recusa pela mesma régua. As frases são as do 400 do servidor:
// `pedido-do-portal.test.ts` lê as duas listas e reprova se divergirem.
import type { ExpertiseMode, ServiceRequestKind } from "./types";

export const FRASES_DO_PEDIDO = {
  processo: "Escolha o processo",
  prazo: "Informe o prazo",
  dataDaPericia: "Informe a data e o horário da perícia",
  modoDaPericia: "Escolha onde será a perícia",
  link: "Informe o link da reunião",
  endereco: "Informe o CEP e o número do local",
  cep: "Informe um CEP válido com 8 dígitos",
  prazoDoLaudo: "Informe o prazo do laudo",
  detalhes: "Informe os detalhes da solicitação",
  anexo: "Anexe o capa a capa ou o documento",
  anexosDemais: "Anexe no máximo 10 arquivos",
} as const;

/** Quantos anexos a solicitação leva — o mesmo número da API (a trava confere). */
export const MAXIMO_DE_ANEXOS = 10;

/** O CEP como a ficha aceita: oito dígitos, com ou sem o hífen (o mesmo da API). */
export const PADRAO_DO_CEP = /^\d{5}-?\d{3}$/u;

export interface PedidoDoPortal {
  kind: ServiceRequestKind;
  processId?: string | null;
  dueDate?: string | null;
  details?: string | null;
  pericia?: {
    date?: string | null;
    time?: string | null;
    mode?: ExpertiseMode | "" | null;
    link?: string | null;
    zip?: string | null;
    number?: string | null;
    reportDueDate?: string | null;
  } | null;
  /** Os anexos: os arquivos escolhidos na tela, ou os ids no demo. */
  documentIds?: readonly unknown[] | null;
}

function preenchido(valor: string | null | undefined): boolean {
  return Boolean(valor?.trim());
}

/** A primeira falta da ficha, na ordem do formulário, ou `null` se está completa. */
export function recusaDoPedidoDoPortal(pedido: PedidoDoPortal): string | null {
  if (!preenchido(pedido.processId)) return FRASES_DO_PEDIDO.processo;
  if (!preenchido(pedido.dueDate)) return FRASES_DO_PEDIDO.prazo;
  if (pedido.kind === "EXPERTISE") {
    const pericia = pedido.pericia ?? {};
    if (!preenchido(pericia.date) || !preenchido(pericia.time)) return FRASES_DO_PEDIDO.dataDaPericia;
    if (!pericia.mode) return FRASES_DO_PEDIDO.modoDaPericia;
    if (pericia.mode === "VIRTUAL" && !preenchido(pericia.link)) return FRASES_DO_PEDIDO.link;
    if (pericia.mode === "ON_SITE" && (!preenchido(pericia.zip) || !preenchido(pericia.number))) {
      return FRASES_DO_PEDIDO.endereco;
    }
    if (pericia.mode === "ON_SITE" && !PADRAO_DO_CEP.test(pericia.zip!.trim())) return FRASES_DO_PEDIDO.cep;
    if (!preenchido(pericia.reportDueDate)) return FRASES_DO_PEDIDO.prazoDoLaudo;
  }
  if (pedido.kind === "OTHER" && !preenchido(pedido.details)) return FRASES_DO_PEDIDO.detalhes;
  if (pedido.kind !== "OTHER" && !(pedido.documentIds?.length)) return FRASES_DO_PEDIDO.anexo;
  if ((pedido.documentIds?.length ?? 0) > MAXIMO_DE_ANEXOS) return FRASES_DO_PEDIDO.anexosDemais;
  return null;
}

/** O rótulo de cada tipo, espelho de `serviceRequestKindLabel`. */
export const ROTULO_DO_TIPO: Record<ServiceRequestKind, string> = {
  EXPERTISE: "Perícia",
  EXPERT_REPORT: "Laudo",
  QUESTIONS: "Quesitos",
  IMPUGNATION: "Impugnação",
  CONSULTING: "Consultoria",
  OTHER: "Outros",
};

/** O resumo que o servidor deriva quando a ficha não traz um (`resumoDoPedido`). */
export function resumoDoPedido(kind: ServiceRequestKind, caseNumber: string): string {
  return `${ROTULO_DO_TIPO[kind]} — ${caseNumber}`;
}
